import { flushPlayback, recordPlayback } from './episodePlayback';

const TRACK_EVENT = 'podeux:audio-track';
export const AUDIO_SOURCE_WILL_CHANGE = 'podeux:audio-source-will-change';

// Register episode metadata without transferring playback to a browsed episode.
export const registerAudioPlaybackTrack = (uid, track) => {
    if (!track?.id || !track.mp3URL) return;
    document.getElementById('audio')?.dispatchEvent(new CustomEvent(TRACK_EVENT, {
        detail: { uid: uid || null, track },
    }));
};

const accountFor = store => {
    const user = store.getState().user;
    return {
        uid: user.authenticated && user.uid ? user.uid : null,
        revision: user.forMeSessionRevision || 0,
        authenticated: user.authenticated,
    };
};
const sameAccount = (left, right) => left.uid === right.uid &&
    left.revision === right.revision && left.authenticated === right.authenticated;

// The shared audio element outlives routes. Its recorder must do so too, while
// retaining the episode/account that actually started this media session.
export const installAudioProgressRecorder = (store, audio = document.getElementById('audio')) => {
    if (!audio) return () => {};
    let active = true;
    let account = accountFor(store);
    let session = null;
    let playback = null;
    const tracks = new Map();
    const current = owner => active && owner === account && sameAccount(owner, accountFor(store));
    const snapshot = () => ({
        seconds: audio.currentTime,
        durationSeconds: Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : undefined,
        completed: audio.ended,
        time: Date.now() / 1000,
    });
    const persist = (sample, actualPlayedAtSeconds, force = false) => {
        if (!session || !sample || !current(session.account)) return;
        if (!force && sample.time - session.lastSaved < 5) return;
        const owner = session.account;
        if (recordPlayback(owner.uid, session.track, 'audio', {
            seconds: sample.seconds,
            durationSeconds: sample.durationSeconds || session.track.totalTimeSeconds,
            completed: sample.completed,
            actualPlayedAtSeconds,
        }, sample.time)) {
            session.lastSaved = sample.time;
            flushPlayback(owner.uid, () => current(owner));
        }
    };
    const checkpoint = () => {
        if (playback && current(playback.account) && playback.source === audio.src) playback.sample = snapshot();
        if (!session) return;
        if (audio.src === session.source) session.sample = snapshot();
        // A source can be replaced directly, without a final pause event. Never
        // read the replacement's position or duration into the previous episode.
        persist(session.sample, undefined, true);
    };
    const retireSource = () => {
        checkpoint();
        session = null;
        playback = null;
    };
    const bindPlayback = () => {
        if (!playback || !current(playback.account) || playback.source !== audio.src) return;
        const track = tracks.get(playback.source);
        if (!track) return;
        if (!session) session = { track, source: playback.source, account: playback.account, lastSaved: 0 };
        session.sample = snapshot();
        persist(session.sample, playback.playedAt, true);
    };
    const register = event => {
        const { uid, track } = event.detail || {};
        if (uid !== account.uid || !track?.id || !track.mp3URL) return;
        const source = new URL(track.mp3URL, document.baseURI).href;
        const metadata = { ...track, transcript: undefined, description: undefined };
        tracks.set(source, metadata);
        if (session?.source === source && session.track.id === track.id) session.track = metadata;
        // Row playback may start before its episode details finish loading.
        if (!session) bindPlayback();
    };
    const playing = () => {
        if (audio.paused || audio.ended || audio.seeking) return;
        if (session && session.source !== audio.src) retireSource();
        playback = { source: audio.src, account, sample: snapshot(), playedAt: Date.now() / 1000 };
        bindPlayback();
    };
    const tick = () => {
        if (session && session.source !== audio.src) retireSource();
        if (!playback || playback.source !== audio.src || !current(playback.account)) return;
        playback.sample = snapshot();
        if (!audio.paused && !audio.seeking && !audio.ended) playback.playedAt = playback.sample.time;
        if (!session) bindPlayback();
        else {
            session.sample = playback.sample;
            if (!audio.paused && !audio.seeking && !audio.ended) persist(session.sample, playback.playedAt);
        }
    };
    const sourceChanged = () => {
        if (session && session.source !== audio.src) retireSource();
        else if (playback && playback.source !== audio.src) playback = null;
    };
    const hidden = () => { if (document.visibilityState === 'hidden') checkpoint(); };
    const accountChanged = () => {
        const next = accountFor(store);
        if (sameAccount(account, next)) return;
        account = next;
        session = null;
        playback = null;
        tracks.clear();
        // Retire ownership before pause events fire. Pending/buffering media
        // must require a new play action before it can belong to another login.
        if (!audio.paused) audio.pause();
    };
    const events = {
        [TRACK_EVENT]: register,
        [AUDIO_SOURCE_WILL_CHANGE]: retireSource,
        playing,
        timeupdate: tick,
        pause: checkpoint,
        ended: checkpoint,
        seeked: checkpoint,
        emptied: sourceChanged,
        loadstart: sourceChanged,
    };
    Object.entries(events).forEach(([name, listener]) => audio.addEventListener(name, listener));
    window.addEventListener('pagehide', checkpoint);
    document.addEventListener('visibilitychange', hidden);
    const unsubscribe = store.subscribe(accountChanged);
    return () => {
        if (!active) return;
        checkpoint();
        active = false;
        unsubscribe();
        Object.entries(events).forEach(([name, listener]) => audio.removeEventListener(name, listener));
        window.removeEventListener('pagehide', checkpoint);
        document.removeEventListener('visibilitychange', hidden);
    };
};
